import { useEffect, useRef, useState } from "react";
import { RefreshCw, X } from "lucide-react";

// Noms de fichiers des bundles JS actuellement chargés (build CRA : /static/js/*.js).
function loadedBundleNames() {
  return new Set(
    Array.from(document.querySelectorAll("script[src]"))
      .map((s) => s.getAttribute("src") || "")
      .filter((src) => src.includes("/static/js/"))
      .map((src) => src.split("/").pop())
  );
}

/**
 * Bannière "Nouvelle version disponible" : compare les bundles JS de la page chargée
 * à ceux référencés par l'index fraîchement récupéré. Si un bundle chargé n'existe plus
 * dans le nouvel index → une publication a eu lieu → on propose de recharger.
 * (Aucun effet en dev : pas de bundles hashés.)
 */
export default function UpdatePrompt() {
  const [show, setShow] = useState(false);
  const loaded = useRef(loadedBundleNames());

  useEffect(() => {
    if (loaded.current.size === 0) return; // serveur de dev : rien à faire
    let stopped = false;

    const check = async () => {
      try {
        const res = await fetch(`/index.html?_=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;
        const html = await res.text();
        const fresh = new Set(
          Array.from(html.matchAll(/\/static\/js\/([^"'\s]+\.js)/g)).map((m) => m[1])
        );
        if (fresh.size === 0) return;
        const outdated = [...loaded.current].some((name) => !fresh.has(name));
        if (!stopped && outdated) setShow(true);
      } catch {}
    };

    const iv = setInterval(check, 60000);
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVis);
    const t = setTimeout(check, 8000);
    return () => {
      stopped = true;
      clearInterval(iv);
      clearTimeout(t);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  const reload = async () => {
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        regs.forEach((r) => r.waiting && r.waiting.postMessage({ type: "SKIP_WAITING" }));
      }
      if (window.caches) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch {}
    window.location.reload();
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-4 inset-x-4 z-[100] flex justify-center pointer-events-none" data-testid="update-prompt">
      <div className="pointer-events-auto w-full max-w-md rounded-2xl bg-obsidian text-white shadow-[0_20px_60px_-15px_rgba(0,0,0,0.6)] px-4 py-3.5 flex items-center gap-3 fade-up">
        <span className="shrink-0 w-9 h-9 rounded-full bg-sage/20 flex items-center justify-center">
          <RefreshCw size={16} className="text-sage" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">Nouvelle version disponible</div>
          <div className="text-xs text-white/55">Recharge pour profiter des dernières améliorations.</div>
        </div>
        <button data-testid="update-reload" onClick={reload}
          className="shrink-0 rounded-full bg-sage text-white px-4 py-2 text-sm font-medium hover:bg-sage/90 transition-colors">
          Mettre à jour
        </button>
        <button data-testid="update-dismiss" onClick={() => setShow(false)}
          className="shrink-0 p-1.5 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-colors">
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
